import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { Grid, Typography, useTheme } from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { faCheck, faQuestion } from "@fortawesome/free-solid-svg-icons";
import {
  faCarBuilding,
  faLaptopHouse,
  faBriefcase,
  faCoffee,
  faChairOffice,
  faStoreAltSlash,
  faExclamationTriangle
} from "@fortawesome/pro-regular-svg-icons";

import { Autocomplete, TextField } from "@aclymatepackages/atoms";
import { numbersRegExpTest } from "@aclymatepackages/reg-exp";
import { formatDate } from "@aclymatepackages/formatters";
import { utilitiesProviders } from "@aclymatepackages/lists";
import {
  incorrectNameCheck,
  splitAggregatedTransactionsBreakdownSubcategories
} from "@aclymatepackages/other-helpers";
import { YesNoQuestion } from "@aclymatepackages/modules";
import { findOfficeCurrentChangeData } from "@aclymatepackages/calcs/helpers";
import { calcAvgEmissionsPastSixMonths } from "@aclymatepackages/date-helpers";

import PlacesAutocomplete from "../../components/inputs/autocomplete/PlacesAutocomplete";
import DatePicker from "../../components/atoms/mui/DatePicker";
import FacilityTypeSelect from "../../components/inputs/offices/FacilityTypeSelect";
import ElectricRenewablesYesNoQuestion from "../../components/inputs/offices/ElectricRenewablesYesNoQuestion";
import ElectricRenewablesPercentageInput from "../../components/inputs/offices/ElectricRenewablesPercentageInput";
import ElectricIntensityYesNoQuestion from "../../components/inputs/offices/ElectricIntensityYesNoQuestion";
import ElectricIntensityAndUnitsInput from "../../components/inputs/offices/ElectricIntensityAndUnitsInput";
import FuelTypeToggleButtons from "../../components/atoms/inputs/FuelTypeToggleButtons";

import { useCompletedOnboardingSteps } from "../hooks/companyData";
import useAccountingData from "../hooks/accountingData";

import { fetchAddressEGrid } from "../utils/apiCalls";
import { analyticsTrack } from "../analytics";
import {
  useAccountData,
  useCachedFirebaseCrud,
  useCachedDisplayData,
  useCollectionDataListener
} from "../firebase";
import { findGoogleAddressStringDetails } from "../utils/geography";
import useEmissionsContext from "../contexts/emissions";
import useDisplayMeasurementSystem from "../hooks/displayMeasurementSystem";
import usePlatformLayoutContext from "../contexts/platformLayout";
import {
  mergeRollupsV2OfficeDocsIntoOffices,
  shouldUseRollupsV2Read
} from "./rollupsV2";

dayjs.extend(utc);

export const electricRenewablesPercentageError = (
  electricRenewablesPercentage
) =>
  electricRenewablesPercentage &&
  (!numbersRegExpTest(electricRenewablesPercentage) ||
    electricRenewablesPercentage > 100);

export const electricRecsPercentageError = (electricRecsPercentage) =>
  electricRecsPercentage &&
  (!numbersRegExpTest(electricRecsPercentage) ||
    electricRecsPercentage < 0 ||
    electricRecsPercentage > 100);

const electricCarbonIntensitySaveEnabled = (
  isElectricCarbonIntensityGiven,
  electricCarbonIntensity,
  electricCarbonIntensityUnits
) =>
  isElectricCarbonIntensityGiven
    ? !electricCarbonIntensityError(electricCarbonIntensity) &&
      electricCarbonIntensityUnits
    : true;

const electricRenewablesPercentageSaveEnabled = (
  isElectricRenewablesPercentageGiven,
  electricRenewablesPercentage
) =>
  isElectricRenewablesPercentageGiven
    ? !electricRenewablesPercentageError(electricRenewablesPercentage)
    : true;

export const electricCarbonIntensityError = (electricCarbonIntensity) =>
  electricCarbonIntensity &&
  (!numbersRegExpTest(electricCarbonIntensity) ||
    electricCarbonIntensity >= 1000000);

export const electricRenewablesAndIntensitySaveEnabled = ({
  isElectricRenewablesPercentageGiven,
  isElectricCarbonIntensityGiven,
  electricRenewablesPercentage,
  electricCarbonIntensity,
  electricCarbonIntensityUnits,
  electricRecsPercentage
}) =>
  electricRenewablesPercentageSaveEnabled(
    isElectricRenewablesPercentageGiven,
    electricRenewablesPercentage
  ) &&
  (electricRenewablesPercentage === "100" ||
    electricCarbonIntensitySaveEnabled(
      isElectricCarbonIntensityGiven,
      electricCarbonIntensity,
      electricCarbonIntensityUnits
    )) &&
  !electricRecsPercentageError(electricRecsPercentage);

const useOfficeCategories = (iconSize = "sm") => {
  const { palette } = useTheme();

  return [
    {
      category: "companyOffice",
      type: "companyOffice",
      name: "Company Office",
      color: palette.offices.main,
      icon: <FontAwesomeIcon icon={faCarBuilding} size={iconSize} />,
      tooltip: "Add a company office for this employee.",
      waypoint: "/images/platform/icons/offices-main.png"
    },
    {
      category: "homeOffice",
      type: "homeOffice",
      name: "Home Office",
      color: palette.offices.light,
      icon: <FontAwesomeIcon icon={faLaptopHouse} size={iconSize} />,
      tooltip: "Add a home office for this employee.",
      waypoint: "/images/platform/icons/offices-light.png"
    },
    {
      category: "otherSpace",
      name: "Other Space",
      icon: <FontAwesomeIcon icon={faBriefcase} size={iconSize} />,
      color: palette.offices.dark,
      tooltip: "Add another space for this employee.",
      waypoint: "/images/platform/icons/offices-dark.png",
      types: [
        {
          name: "Coffee Shop",
          type: "coffeeShop",
          icon: <FontAwesomeIcon icon={faCoffee} size={iconSize} />
        },
        {
          type: "coworking",
          name: "Coworking Space",
          icon: <FontAwesomeIcon icon={faChairOffice} size={iconSize || "sm"} />
        }
      ]
    }
  ];
};

export const useAllOfficeTypes = (iconSize) => {
  const officeCategories = useOfficeCategories(iconSize);

  return officeCategories.flatMap(({ types, ...otherCategoryProps }) => {
    if (!types) {
      return otherCategoryProps;
    }

    return types.map((typeObj) => ({ ...otherCategoryProps, ...typeObj }));
  });
};

export const useOfficeTypes = (params = {}) => {
  const { type, iconSize } = params;
  const officeCategories = useOfficeCategories(iconSize);

  const officeTypes = officeCategories.flatMap(({ types, ...otherProps }) => {
    if (!types) {
      return { ...otherProps };
    }
    return types.map((type) => ({ ...otherProps, ...type }));
  });

  if (type) {
    return officeTypes.find((office) => office.type === type);
  }

  return officeTypes;
};

export const useOfficeStatuses = (endDate) => {
  const { palette } = useTheme();

  return {
    closed: {
      id: "closed",
      icon: faStoreAltSlash,
      tooltip: `This office hasn't been in use since ${formatDate(endDate)}`,
      emissionsDetailsTooltip: `This office hasn't been in use since ${formatDate(
        endDate
      )} and is considered closed.`,
      color: palette.error.main,
      name: "Closed offices",
      severity: 0
    },
    missingEmissions: {
      id: "missingEmissions",
      icon: faExclamationTriangle,
      tooltip:
        "We're using default values for some of this office's emissions.",
      emissionsDetailsTooltip:
        "We're using default values for some of this office's emissions. You can fix this by adding more emissions for this office below.",
      color: palette.mileage.main,
      name: "Offices with default emissions",
      severity: 3
    },
    noEmployees: {
      id: "noEmployees",
      icon: faQuestion,
      tooltip:
        "This office doesn't have any current commute employees. Make sure it's still in use.",
      emissionsDetailsTooltip:
        "This office doesn't have any current commute employees. Make sure it's still in use by adding or editing employees to commute here.",
      color: palette.mileage.main,
      name: "Offices with no employees",
      severity: 2
    },
    active: {
      id: "active",
      icon: faCheck,
      tooltip: "This office is currently in active use by employees",
      emissionsDetailsTooltip:
        "This office is currently in active use by employees",
      color: palette.secondary.main,
      severity: 1,
      name: "Active Offices"
    }
  };
};

export const findUtilitiesConfig = (utilities) => {
  const { combinedUtility, gasUtility, electricUtility } = utilities || {};

  if (combinedUtility) {
    return "combinedUtilities";
  }
  if (gasUtility || electricUtility) {
    return "separateUtilities";
  }
  return "none";
};

//Every emissions calc treats a missing heatFuelType as natural gas —
//calcOfficeUtilitiesPerSqFootage defaults the param and calcGasEmissionsPerUnitValue
//falls through to natural-gas factors — so anything needing a fallback resolves it
//through here rather than picking its own
export const DEFAULT_HEAT_FUEL_TYPE = "naturalGas";

//heatFuelType was never required on stored offices, but the calcs treat its
//absence as natural gas, so edit state resolves it up front instead of letting
//the HEAT tab show a fuel type that no saved value or pending change backs
export const resolveHeatFuelType = (utilities) => {
  const { hasHeat, heatFuelType } = utilities || {};

  if (hasHeat === false) {
    return undefined;
  }

  return heatFuelType || DEFAULT_HEAT_FUEL_TYPE;
};

// The modeled per-sq-ft utilities estimate (monthlyUtilitiesEmissionsTons)
// exists only to fill months the real aggregated transaction cache doesn't
// cover -- it's computed for every month of a leased office's lifetime with
// no awareness of which months already have real bill/transaction data. Once
// real data lands for a given month/subcategory, the modeled estimate for
// that same month/subcategory must be dropped instead of summed alongside
// it, or a leased facility's emissions get double-counted.
export const filterModeledEmissionsWithoutAggregatedOverlap = (
  modeledEmissions,
  aggregatedEmissions
) =>
  modeledEmissions.filter(
    ({ subcategory, date }) =>
      !aggregatedEmissions.some(
        (aggregatedEmission) =>
          aggregatedEmission.subcategory === subcategory &&
          dayjs(aggregatedEmission.date).isSame(date, "month")
      )
  );

// Phase 3 read-cutover (per-company canary), same rollupsV2ReadEnabled flag
// and pattern as EmissionsContextProvider's vendor listener -- but offices
// are structurally different from vendors/employees/events: legacy
// `aggregatedTransactions` lives embedded ON each office doc (from the plain
// "offices" collection), not a parallel collection, so this can't just swap
// which collection it listens to -- it has to fetch rollups-v2-offices
// alongside "offices" and merge the result back onto each office object (see
// rollupsV2.js's mergeRollupsV2OfficeDocsIntoOffices for why: rollups-v2
// moved the office bucket into its own collection "for consistency with the
// other four bucket types"). The monthKey >= startDate filter only applies
// on the rollups-v2 side for the same reason it does for vendors/employees/
// events: nothing upstream on that write path excludes a transaction for
// predating the company's startDate, whereas the legacy embedded field is
// populated by the aggregation cron's own date >= startDate query and so
// never needs it.
export const useOfficesWithCurrentChanges = () => {
  const [offices, officesLoading] = useCollectionDataListener("offices");
  const [{ startDate, rollupsV2ReadEnabled }] = useAccountData();
  const { viewMode } = usePlatformLayoutContext() || {};
  const effectiveRollupsV2ReadEnabled = shouldUseRollupsV2Read(
    rollupsV2ReadEnabled,
    viewMode
  );

  // Unlike vendors/employees/events (a single useCollectionDataListener call
  // toggling WHICH collection it points at), this is a second listener --
  // offices always needs the base "offices" collection read regardless of
  // migration status, so there's no single call to toggle. It must not be
  // *always-on*, though: rollupsV2Enabled (the write-side flag) dual-writes
  // to all five rollups-v2-* collections, including rollups-v2-offices,
  // well before rollupsV2ReadEnabled is ever flipped on for that company
  // (confirmed against rollupWrites.js -- the write path checks only
  // rollupsV2Enabled, with no rollupsV2ReadEnabled awareness at all). A
  // company in that in-between canary state is not missing this
  // subcollection; it has a real, continuously-updating one, so an
  // unconditional query here would attach a real, ever-growing listener and
  // discard every snapshot it downloads. Passing a falsy collection to
  // useCollectionDataListener disables the listener entirely instead.
  const officeRollupsQueries = effectiveRollupsV2ReadEnabled
    ? [["monthKey", ">=", dayjs.utc(startDate).format("YYYY-MM")]]
    : [];
  const [rollupsV2OfficeDocs, officeRollupsLoading] = useCollectionDataListener(
    effectiveRollupsV2ReadEnabled ? "rollups-v2-offices" : null,
    officeRollupsQueries
  );

  if (
    officesLoading ||
    (effectiveRollupsV2ReadEnabled && officeRollupsLoading)
  ) {
    return [[], true];
  }

  if (!offices.length) {
    return [[], false];
  }

  const officesWithAggregatedTransactions = effectiveRollupsV2ReadEnabled
    ? mergeRollupsV2OfficeDocsIntoOffices(offices, rollupsV2OfficeDocs)
    : offices;

  const officesWithChanges = officesWithAggregatedTransactions.map((office) =>
    findOfficeCurrentChangeData(office)
  );

  const officesWithUtilitiesConfig = officesWithChanges.map((office) => {
    const { currentUtilities } = office;
    const utilitiesConfig = findUtilitiesConfig(currentUtilities);

    return { ...office, utilitiesConfig };
  });

  return [officesWithUtilitiesConfig, false];
};

export const setOfficeAddress = (setEndpoint, setName) => async (place) => {
  if (place) {
    const { place_id, description } = place;
    const { zipCode, state, country, name, coordinates } =
      await findGoogleAddressStringDetails(place_id);

    if (setName && name) {
      setEndpoint("name")(name);
    }

    return setEndpoint("address")({
      description: description,
      zipCode,
      state,
      country,
      coordinates,
      placeId: place_id
    });
  }
  return setEndpoint("address")(null);
};

export const useBuildNewOfficeInputFormRows = () => {
  const [{ name: companyName, startDate: companyStartDate }] = useAccountData();
  const [{ earliestNewEmissionDate }] = useAccountingData();

  return ({ office, editOffice, index }) => {
    const {
      name,
      address,
      electricityInLease,
      hasHeat,
      heatInLease,
      heatFuelType,
      isGasSameAsElectricUtility,
      sqFootage,
      electricUtility,
      gasUtility = null,
      isElectricRenewablesPercentageGiven,
      electricRenewablesPercentage,
      isElectricCarbonIntensityGiven,
      electricCarbonIntensity,
      electricCarbonIntensityUnits,
      electricRecsPercentage,
      hasNoStartDate,
      startDate,
      hasNoEndDate,
      endDate,
      facilityType
    } = office;

    const addNewOfficeAddress = (field) => (value) => editOffice(field, value);

    const officeNameInputRow = {
      label: "What do you call this office?",
      value: name && !incorrectNameCheck(name),
      input: (
        <TextField
          label="Office Name"
          value={name}
          setValue={(value) => editOffice("name", value)}
          error={name && incorrectNameCheck(name)}
          helperText={
            name &&
            incorrectNameCheck(name) &&
            "You need to have at least one non-numeric character in the office name and the name cannot have more than 50 characters"
          }
        />
      )
    };

    const officeAddressInputRow = {
      label: "What's the address?",
      value: address,
      input: (
        <PlacesAutocomplete
          label="What's the address?"
          place={address || null}
          editPlace={setOfficeAddress(addNewOfficeAddress)}
          size="small"
          id={`NewOffice_${index}_Address`}
        />
      )
    };

    const electricityInLeaseInputRow = {
      label: "Is electricity included in the lease?",
      value: electricityInLease !== undefined,
      input: (
        <YesNoQuestion
          question="Is electricity included in the lease?"
          value={electricityInLease}
          setValue={(value) => editOffice("electricityInLease", value)}
        />
      )
    };

    const hasHeatInputRow = {
      label: "Does this office have heat?",
      value: hasHeat !== undefined,
      input: (
        <YesNoQuestion
          question="Does this office have heat?"
          value={hasHeat}
          setValue={(value) => editOffice("hasHeat", value)}
        />
      )
    };

    const heatInLeaseInputRow = {
      label: "Is heat included in the lease?",
      value: hasHeat === false ? true : heatInLease !== undefined,
      input: (
        <YesNoQuestion
          question="Is heat included in the lease?"
          value={heatInLease}
          setValue={(value) => editOffice("heatInLease", value)}
          disabled={hasHeat === false}
        />
      )
    };

    const heatFuelTypeInputRow = {
      label: "What fuel type does this office use for heat?",
      value: hasHeat === false ? true : !!heatFuelType,
      input: (
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <Typography variant="body1">Heating Fuel Type: </Typography>
          </Grid>
          <Grid item>
            <FuelTypeToggleButtons
              value={heatFuelType}
              onChange={(value) => editOffice("heatFuelType", value)}
              disabled={hasHeat === false}
            />
          </Grid>
        </Grid>
      )
    };

    const handleSetIsGasSameAsElectricUtility = (
      isGasSameAsElectricUtility
    ) => {
      editOffice("isGasSameAsElectricUtility", isGasSameAsElectricUtility);
      editOffice("electricUtility", gasUtility);
      return editOffice(
        "combinedUtility",
        isGasSameAsElectricUtility ? gasUtility : null
      );
    };

    const filteredUtilitiesProviders = utilitiesProviders.filter(
      ({ states, utilityId }) =>
        states.includes(address?.state) ||
        (process.env.REACT_APP_ENVIRONMENT !== "production" &&
          utilityId === "DEMO")
    );

    const showGasUtilityInput = hasHeat && heatInLease === false;
    const showSameAsElectricToggle =
      showGasUtilityInput && gasUtility && electricityInLease === false;
    const showElectricUtilityInput =
      electricityInLease === false &&
      (!showGasUtilityInput || isGasSameAsElectricUtility === false);

    const utilitiesProvidersInputRow = {
      label: "Utility providers",
      value: (() => {
        const gasComplete =
          !showGasUtilityInput || !!gasUtility || isGasSameAsElectricUtility;
        const electricComplete =
          electricityInLease !== false ||
          !!electricUtility ||
          isGasSameAsElectricUtility;
        return gasComplete && electricComplete;
      })(),
      input: (
        <Grid container spacing={2} direction="column">
          {showGasUtilityInput && (
            <>
              <Grid item>
                <Typography variant="body2">
                  Please select your gas utility from this list, or type in the
                  name on your bills if you don&apos;t see your utility.
                </Typography>
              </Grid>
              <Grid item>
                <Autocomplete
                  label="Select gas utility"
                  id="gas-utility-provider-autocomplete"
                  availableOptions={filteredUtilitiesProviders}
                  value={gasUtility}
                  setValue={(selectedUtility) =>
                    editOffice(
                      "gasUtility",
                      typeof selectedUtility === "string"
                        ? { name: selectedUtility }
                        : selectedUtility
                    )
                  }
                  freeSolo
                  autoSelect
                />
              </Grid>
              {showSameAsElectricToggle && (
                <Grid item>
                  <YesNoQuestion
                    question="Is this the same utility you get electricity from?"
                    value={isGasSameAsElectricUtility}
                    setValue={(value) =>
                      handleSetIsGasSameAsElectricUtility(value)
                    }
                  />
                </Grid>
              )}
            </>
          )}
          {showElectricUtilityInput && (
            <>
              <Grid item>
                <Typography variant="body2">
                  Please select your electric utility from this list, or type in
                  the name on your bills if you don&apos;t see your utility.
                </Typography>
              </Grid>
              <Grid item>
                <Autocomplete
                  label="Select electric utility"
                  id="electric-utility-provider-autocomplete"
                  availableOptions={filteredUtilitiesProviders}
                  value={electricUtility}
                  setValue={(selectedUtility) =>
                    editOffice(
                      "electricUtility",
                      typeof selectedUtility === "string"
                        ? { name: selectedUtility }
                        : selectedUtility
                    )
                  }
                  freeSolo
                  autoSelect
                />
              </Grid>
            </>
          )}
        </Grid>
      )
    };

    const officeSquareFootageInputRow = {
      label: "What's the square footage?",
      value: numbersRegExpTest(sqFootage) && sqFootage < 1000000,
      input: (
        <TextField
          required
          label="What's the square footage?"
          value={sqFootage}
          setValue={(value) => editOffice("sqFootage", value)}
          error={
            sqFootage && (!numbersRegExpTest(sqFootage) || sqFootage >= 1000000)
          }
          helperText={
            sqFootage &&
            (!numbersRegExpTest(sqFootage) || sqFootage >= 1000000) &&
            "You need to input a number for the square footage and/or the square footage needs to be less than 1,000,000"
          }
          id={`NewOffice_${index}_SqFootage`}
        />
      )
    };

    const officeFacilityTypeInputRow = {
      value: facilityType?.value,
      input: (
        <FacilityTypeSelect
          facilityType={facilityType}
          editFacilityType={(value) => editOffice("facilityType", value)}
          label="What type of facility is this?"
        />
      )
    };

    // Same shape as usePersonalInfoForm's employee-onboarding shortcut: this
    // question only makes sense when companyStartDate is a real boundary to
    // compare against. Without one, answering Yes skips the actual move-in
    // DatePicker below and useNewOfficesSubmit persists this office with no
    // startDate at all -- once a company startDate is later derived,
    // recurring office emissions can then be generated all the way back to
    // that later boundary even if this office opened after it, overstating
    // historical emissions for it.
    const buildOfficeStartDateCheckBoxInputRow =
      !earliestNewEmissionDate && companyStartDate
        ? [
            {
              input: (
                <YesNoQuestion
                  question={`${companyName} started at this office before ${formatDate(
                    companyStartDate
                  )}`}
                  value={hasNoStartDate}
                  setValue={(value) => editOffice("hasNoStartDate", value)}
                />
              ),
              value: hasNoStartDate !== undefined
            }
          ]
        : [];

    const buildOfficeMoveInDateInputRow = !hasNoStartDate
      ? [
          {
            input: (
              <DatePicker
                label="When did you move into this office?"
                darkTheme
                date={startDate || earliestNewEmissionDate || companyStartDate}
                minDate={earliestNewEmissionDate || companyStartDate}
                maxDate={new Date()}
                editDate={(date) => editOffice("startDate", new Date(date))}
              />
            ),
            // Same resolved value the DatePicker itself displays -- this row
            // is "complete" once any of those three dates exist, not only
            // when the company-level startDate specifically does. Before
            // startDate could be genuinely unset, companyStartDate was
            // always truthy here regardless of what the user picked; now
            // that it can be missing, gating completion on it alone traps
            // this row incomplete even after a real move-in date is chosen.
            value: startDate || earliestNewEmissionDate || companyStartDate
          }
        ]
      : [];

    const buildOfficeStillWorksAtOfficeCheckBoxInputRow = [
      {
        input: (
          <YesNoQuestion
            question={`${companyName} still works at this office`}
            value={hasNoEndDate}
            setValue={(value) => editOffice("hasNoEndDate", value)}
          />
        ),
        value: hasNoEndDate !== undefined
      }
    ];

    const buildOfficeMoveOutDateInputRow = !hasNoEndDate
      ? [
          {
            input: (
              <DatePicker
                label="When did you move out of this office?"
                darkTheme={true}
                date={endDate}
                minDate={startDate}
                maxDate={new Date()}
                editDate={(date) => editOffice("endDate", new Date(date))}
              />
            ),
            value: endDate
          }
        ]
      : [];

    const officeElectricRenewablesPercentCheckBoxInputRow = {
      input: (
        <ElectricRenewablesYesNoQuestion
          questionText={
            "Are you getting part of your electric utilities from renewable energy sources?"
          }
          isElectricRenewablesPercentageGiven={
            isElectricRenewablesPercentageGiven
          }
          editIsElectricRenewablesPercentageGiven={(value) =>
            editOffice("isElectricRenewablesPercentageGiven", value)
          }
        />
      ),
      value: isElectricRenewablesPercentageGiven !== undefined
    };

    const buildOfficeElectricRenewablesPercentInputRow =
      isElectricRenewablesPercentageGiven
        ? [
            {
              label:
                "Percentage of electric utilities that come from renewables",
              input: (
                <ElectricRenewablesPercentageInput
                  electricRenewablesPercentage={electricRenewablesPercentage}
                  editElectricRenewablesPercentage={(value) =>
                    editOffice("electricRenewablesPercentage", value)
                  }
                  index={index}
                />
              ),
              value:
                electricRenewablesPercentage &&
                !electricRenewablesPercentageError(electricRenewablesPercentage)
            }
          ]
        : [];

    const buildOfficeElectricCarbonIntensityCheckBoxInputRow =
      electricRenewablesPercentage !== "100"
        ? [
            {
              input: (
                <ElectricIntensityYesNoQuestion
                  questionText={
                    "Do you know the carbon intensity of your electric utilities that don't come from renewables?"
                  }
                  isElectricCarbonIntensityGiven={
                    isElectricCarbonIntensityGiven
                  }
                  editIsElectricCarbonIntensityGiven={(value) =>
                    editOffice("isElectricCarbonIntensityGiven", value)
                  }
                />
              ),
              value: isElectricCarbonIntensityGiven !== undefined
            }
          ]
        : [];

    const buildOfficeElectricCarbonIntensityInputRow =
      isElectricCarbonIntensityGiven
        ? [
            {
              label: "Carbon intensity of electricity utilities",
              input: (
                <Grid container direction="row" spacing={2}>
                  <ElectricIntensityAndUnitsInput
                    electricCarbonIntensity={electricCarbonIntensity}
                    editElectricCarbonIntensity={(value) =>
                      editOffice("electricCarbonIntensity", value)
                    }
                    electricCarbonIntensityUnits={electricCarbonIntensityUnits}
                    editElectricCarbonIntensityUnits={(value) =>
                      editOffice("electricCarbonIntensityUnits", value)
                    }
                    index={index}
                  />
                </Grid>
              ),
              value:
                electricCarbonIntensity &&
                !electricCarbonIntensityError(electricCarbonIntensity) &&
                electricCarbonIntensityUnits
            }
          ]
        : [];

    const officeElectricRecsPercentageInputRow = {
      label: "Percentage of electricity from RECs",
      input: (
        <TextField
          label="Percentage of electricity from RECs"
          value={electricRecsPercentage}
          setValue={(value) => editOffice("electricRecsPercentage", value)}
          error={electricRecsPercentageError(electricRecsPercentage)}
          helperText={
            electricRecsPercentageError(electricRecsPercentage)
              ? "Please enter a percentage between 0 and 100"
              : ""
          }
        />
      ),
      value:
        electricRecsPercentage !== undefined &&
        !electricRecsPercentageError(electricRecsPercentage)
    };

    const buildUtilitiesProvidersInputRow = () =>
      showGasUtilityInput || showElectricUtilityInput
        ? [utilitiesProvidersInputRow]
        : [];

    const newOfficeForm = [
      officeNameInputRow,
      officeAddressInputRow,
      officeSquareFootageInputRow,
      officeFacilityTypeInputRow,
      electricityInLeaseInputRow,
      officeElectricRenewablesPercentCheckBoxInputRow,
      ...buildOfficeElectricRenewablesPercentInputRow,
      ...buildOfficeElectricCarbonIntensityCheckBoxInputRow,
      ...buildOfficeElectricCarbonIntensityInputRow,
      officeElectricRecsPercentageInputRow,
      hasHeatInputRow,
      heatInLeaseInputRow,
      heatFuelTypeInputRow,
      ...buildUtilitiesProvidersInputRow(),
      ...buildOfficeStartDateCheckBoxInputRow,
      ...buildOfficeMoveInDateInputRow,
      ...buildOfficeStillWorksAtOfficeCheckBoxInputRow,
      ...buildOfficeMoveOutDateInputRow
    ];

    return newOfficeForm;
  };
};

export const useNewOfficesSubmit = () => {
  const { updateCollectionDoc, newCollectionDoc, updateAccountData } =
    useCachedFirebaseCrud();
  const [{ primaryOffice, isRemote }] = useAccountData();

  const editCompanyData = async (newOfficeIds, newOffices) => {
    if (!primaryOffice) {
      const [firstOfficeId] = newOfficeIds;
      const [{ address, name }] = newOffices;

      const eGrid = await fetchAddressEGrid(address);

      return updateAccountData({
        address: { ...address, eGrid },
        isRemote: false,
        primaryOffice: { name, id: firstOfficeId }
      });
    }

    if (isRemote) {
      return updateAccountData({ isRemote: false });
    }

    return { newOffices, newOfficeIds };
  };

  return async (newOffices) => {
    analyticsTrack("Offices Added", { numberOfOffices: newOffices.length });

    const buildNewOfficeObj = async (office) => {
      const {
        name,
        sqFootage,
        address,
        facilityType,
        electricUtility: formElectricUtility,
        gasUtility: formGasUtility,
        electricRenewablesPercentage,
        electricCarbonIntensity,
        electricCarbonIntensityUnits,
        electricRecsPercentage,
        electricityInLease,
        hasHeat,
        heatInLease,
        heatFuelType,
        isGasSameAsElectricUtility,
        startDate,
        endDate
      } = office;

      const eGrid = await fetchAddressEGrid(address);

      const buildOfficeUtilitiesObj = () => {
        const hasHeatUtility = hasHeat !== false;
        const electricityInLeaseValue = electricityInLease === true;
        const heatInLeaseValue = hasHeatUtility && heatInLease === true;
        const utilitiesInLease =
          electricityInLeaseValue && (!hasHeatUtility || heatInLeaseValue);

        const baseReturnObj = {
          eGrid,
          electricityInLease: electricityInLeaseValue,
          hasHeat: hasHeatUtility,
          heatInLease: heatInLeaseValue,
          heatFuelType,
          utilitiesInLease,
          doesOfficeHaveGasUtility: hasHeatUtility,
          isGasInIncludedUtilities: heatInLeaseValue
        };

        const getNameFromUtilityObj = (utility) =>
          utility?.name || utility || "None";

        const shouldIncludeElectricUtility = electricityInLeaseValue === false;
        const shouldIncludeGasUtility =
          hasHeatUtility && heatInLeaseValue === false;

        const electricUtility = shouldIncludeElectricUtility
          ? getNameFromUtilityObj(formElectricUtility)
          : undefined;
        const gasUtility = shouldIncludeGasUtility
          ? getNameFromUtilityObj(formGasUtility)
          : undefined;

        if (!hasHeatUtility || !shouldIncludeGasUtility) {
          return {
            ...baseReturnObj,
            ...(electricUtility ? { electricUtility } : {})
          };
        }

        const combinedUtilityName = gasUtility || electricUtility;
        if (isGasSameAsElectricUtility && combinedUtilityName) {
          return {
            ...baseReturnObj,
            combinedUtility: combinedUtilityName
          };
        }

        return {
          ...baseReturnObj,
          ...(gasUtility ? { gasUtility } : {}),
          ...(electricUtility ? { electricUtility } : {})
        };
      };

      return {
        name,
        sqFootage: Number(sqFootage),
        address,
        archived: false,
        type: "companyOffice",
        facilityType,
        startDate,
        endDate,
        electricRenewablesPercentage,
        electricCarbonIntensity,
        electricCarbonIntensityUnits,
        electricRecsPercentage,
        utilities: buildOfficeUtilitiesObj()
      };
    };

    const newOfficeIds = await Promise.all(
      newOffices.map(async (office) => {
        const { id } = office;
        const newOfficeObj = await buildNewOfficeObj(office);

        if (id) {
          await updateCollectionDoc("offices", id, newOfficeObj);
          return id;
        }

        return await newCollectionDoc("offices", newOfficeObj);
      })
    );

    return editCompanyData(newOfficeIds, newOffices);
  };
};

// Bare dayjs() on both sides, deliberately -- safe per #2119's model. The
// `emissions` this is called with (findOfficeTotalMonthlyCarbonVolumes
// below) mixes local-anchored calcs/recurring entries (self-consistent
// under a local read) with transaction/rollup-month entries whose date
// carries the +1-day margin documented in correctDateForAggregation /
// reshapeRollupsV2MonthDocsToLegacyShape, which is what keeps a local
// read safe for those too. Do not "fix" this to dayjs.utc().
export const findEmissionsInLastFullMonth = (emissions) => {
  if (!emissions.length) {
    return [];
  }
  const lastMonth = dayjs().subtract(1, "month");

  return emissions.filter(({ date }) => dayjs(date).isSame(lastMonth, "month"));
};

const RECURRING_CATEGORY_ACCUMULATOR_KEYS = {
  "upstream-leased-assets": "monthlyLeasedUtilitiesTons",
  "purchased-heat": "monthlyPurchasedHeatTons",
  electricity: "monthlyElectricCarbonTons",
  gas: "monthlyGasCarbonTons",
  "employee-commuting": "monthlyCommuteEmissionsTons"
};

// Hoisted to module scope (previously a closure inside useOfficesData's
// formatDisplayOffices, which only ever used it as a pure function of its
// arguments) so it can be exported and unit-tested directly. convertCarbonUnits
// is a parameter rather than a closure over useDisplayMeasurementSystem() for
// the same reason.
export const findOfficeTotalMonthlyCarbonVolumes = ({
  utilities,
  emissions,
  convertCarbonUnits
}) => {
  const { hasHeat } = utilities || {};

  const recurringCategories = Object.keys(RECURRING_CATEGORY_ACCUMULATOR_KEYS);

  const recurringEmissionsInLastFullMonth = findEmissionsInLastFullMonth(
    emissions
  ).filter(({ subcategory }) => recurringCategories.includes(subcategory));

  const nonRecurringEmissions = emissions.filter(
    ({ subcategory }) => !recurringCategories.includes(subcategory)
  );

  if (
    !recurringEmissionsInLastFullMonth.length &&
    !nonRecurringEmissions.length
  ) {
    return {
      monthlyElectricCarbonVolume: 0,
      monthlyGasCarbonVolume: 0,
      totalMonthlyCarbonVolume: 0,
      monthlyCommuteEmissionsTons: 0,
      monthlyLeasedUtilitiesTons: 0
    };
  }

  if (
    recurringEmissionsInLastFullMonth.length >= 1 ||
    nonRecurringEmissions.length >= 1
  ) {
    const recurringEmissionSums = recurringEmissionsInLastFullMonth.reduce(
      (acc, emission) => {
        const {
          subcategory,
          tonsCo2e,
          electricRenewablesReplacementTons = 0
        } = emission;

        const accumulatorKey = RECURRING_CATEGORY_ACCUMULATOR_KEYS[subcategory];

        return {
          ...acc,
          ...(accumulatorKey
            ? { [accumulatorKey]: acc[accumulatorKey] + tonsCo2e }
            : {}),
          monthlyElectricRenewablesReplacementTons:
            acc.monthlyElectricRenewablesReplacementTons +
            electricRenewablesReplacementTons
        };
      },
      {
        monthlyLeasedUtilitiesTons: 0,
        monthlyPurchasedHeatTons: 0,
        monthlyElectricCarbonTons: 0,
        monthlyElectricRenewablesReplacementTons: 0,
        monthlyGasCarbonTons: 0,
        monthlyCommuteEmissionsTons: 0
      }
    );

    const {
      monthlyElectricCarbonTons,
      monthlyElectricRenewablesReplacementTons,
      monthlyLeasedUtilitiesTons,
      monthlyPurchasedHeatTons,
      monthlyGasCarbonTons,
      monthlyCommuteEmissionsTons
    } = recurringEmissionSums;

    const nonRecurringEmissionsSubcategories = [
      ...new Set(nonRecurringEmissions.map((e) => e.subcategory))
    ];

    const nonRecurringEmissionsAverages =
      nonRecurringEmissionsSubcategories.reduce((acc, subcategory) => {
        const subcategoryEmissions = emissions.filter(
          (emission) => emission.subcategory === subcategory
        );

        const sixMonthAverage =
          calcAvgEmissionsPastSixMonths(subcategoryEmissions);

        return {
          ...acc,
          [subcategory]: convertCarbonUnits(sixMonthAverage)
        };
      }, {});

    const monthlyLeasedUtilitiesVolume = convertCarbonUnits(
      monthlyLeasedUtilitiesTons
    );
    const monthlyPurchasedHeatVolume = convertCarbonUnits(
      hasHeat === false ? 0 : monthlyPurchasedHeatTons
    );
    const monthlyGasCarbonVolume = convertCarbonUnits(
      hasHeat === false ? 0 : monthlyGasCarbonTons
    );
    const monthlyElectricCarbonVolume = convertCarbonUnits(
      monthlyElectricCarbonTons
    );
    const monthlyElectricRenewablesReplacementVolume = convertCarbonUnits(
      monthlyElectricRenewablesReplacementTons
    );
    const monthlyCommuteEmissionsVolume = convertCarbonUnits(
      monthlyCommuteEmissionsTons
    );

    return {
      monthlyRecurringEmissions: {
        "upstream-leased-assets": monthlyLeasedUtilitiesVolume,
        "purchased-heat": monthlyPurchasedHeatVolume,
        electricity: monthlyElectricCarbonVolume,
        gas: monthlyGasCarbonVolume,
        "employee-commuting": monthlyCommuteEmissionsVolume,
        "renewable-energy": monthlyElectricRenewablesReplacementVolume
      },
      nonRecurringEmissionsAverages,
      totalMonthlyCarbonVolume:
        monthlyElectricCarbonVolume +
        monthlyGasCarbonVolume +
        monthlyCommuteEmissionsVolume +
        monthlyLeasedUtilitiesVolume +
        monthlyPurchasedHeatVolume +
        Object.values(nonRecurringEmissionsAverages).reduce(
          (sum, singleEmissionVolume) => sum + singleEmissionVolume,
          0
        )
    };
  }
};

export const useOfficesData = () => {
  const officeStatuses = useOfficeStatuses();

  const {
    emissionsLoading,
    separateUtilitiesOfficesDefaultEmissions,
    monthlyUtilitiesEmissionsTons,
    monthlyCommuteEmissionsTons
  } = useEmissionsContext();
  const { convertCarbonUnits } = useDisplayMeasurementSystem();

  const [, onboardingDataLoading] = useCompletedOnboardingSteps();
  const [confirmedEmployees, employeesLoading] = useCachedDisplayData(
    "employees",
    [["status", "==", "confirmed"]]
  );
  const [offices, officesLoading] = useOfficesWithCurrentChanges();

  const componentLoading =
    emissionsLoading ||
    officesLoading ||
    employeesLoading ||
    onboardingDataLoading;

  const formatDisplayOffices = () => {
    if (componentLoading) {
      return [];
    }

    const currentCompanyOfficeEndpoints = confirmedEmployees.flatMap(
      ({ id, name, commuteSchedules }) => {
        if (!Array.isArray(commuteSchedules) || !commuteSchedules.length) {
          return [];
        }

        const [{ commuteEndpoints: currentEndpoints = [] }] = commuteSchedules;
        if (!Array.isArray(currentEndpoints)) {
          return [];
        }

        return currentEndpoints
          .filter(({ type }) => type === "companyOffice")
          .map((endpoint) => ({
            ...endpoint,
            employeeId: id,
            employeeName: name
          }));
      }
    );

    const findOfficeStatus = ({
      endDate,
      currentUtilities,
      emissions,
      currentCommuteEmployees
    }) => {
      if (endDate) {
        return "closed";
      }

      const { electricityInLease, heatInLease, hasHeat } =
        currentUtilities || {};

      const doesOfficeHaveDefaultEmissions = emissions.some(
        ({ source }) => source === "default-utilities"
      );

      const powerNotInLease = electricityInLease === false;
      const heatNotInLease = hasHeat !== false && heatInLease === false;

      if (
        (powerNotInLease || heatNotInLease) &&
        doesOfficeHaveDefaultEmissions
      ) {
        return "missingEmissions";
      }

      if (!currentCommuteEmployees.length) {
        return "noEmployees";
      }

      return "active";
    };

    return offices.map((office) => {
      const {
        id,
        facilityType,
        endDate,
        currentUtilities,
        sqFootage,
        address,
        electricCarbonIntensity,
        electricCarbonIntensityUnits,
        electricRenewablesPercentage,
        aggregatedTransactions = []
      } = office;

      const currentCommuteEmployees = currentCompanyOfficeEndpoints
        ? currentCompanyOfficeEndpoints
            .filter((endpoint) => endpoint.id === id)
            .map(({ daysPerWk, employeeId, employeeName }) => ({
              daysPerWk,
              id: employeeId,
              name: employeeName
            }))
        : [];

      const officeTaggedAggregatedEmissions =
        splitAggregatedTransactionsBreakdownSubcategories(
          aggregatedTransactions
        );

      const officeDefaultEmissions =
        separateUtilitiesOfficesDefaultEmissions.filter(
          ({ taggedOfficeId }) => taggedOfficeId === id
        );

      const taggedUtilitiesEmissions = monthlyUtilitiesEmissionsTons.filter(
        ({ office }) => office?.id === id
      );

      const taggedUtilitiesEmissionsWithoutAggregatedOverlap =
        filterModeledEmissionsWithoutAggregatedOverlap(
          taggedUtilitiesEmissions,
          officeTaggedAggregatedEmissions
        );

      const utilitiesEmissionsWithRenewables =
        taggedUtilitiesEmissionsWithoutAggregatedOverlap
          .map((emission) => {
            const { electricRenewablesReplacementTons, details } = emission;
            const {
              electricRenewablesReplacementTons: detailsReplacementTons
            } = details || {};

            if (!electricRenewablesReplacementTons && !detailsReplacementTons) {
              return emission;
            }

            return [
              emission,
              {
                ...emission,
                subcategory: "renewable-energy",
                tonsCo2e:
                  electricRenewablesReplacementTons || detailsReplacementTons
              }
            ];
          })
          .flat();

      const officeRecurringCommuteEmissions =
        monthlyCommuteEmissionsTons.filter(({ office }) => office?.id === id);

      const emissions = [
        ...officeRecurringCommuteEmissions,
        ...officeTaggedAggregatedEmissions,
        ...officeDefaultEmissions,
        ...utilitiesEmissionsWithRenewables
      ];

      const status = findOfficeStatus({
        endDate,
        currentUtilities,
        emissions,
        currentCommuteEmployees
      });

      const { severity } = officeStatuses[status];

      const monthlyCarbonVolumes = findOfficeTotalMonthlyCarbonVolumes({
        utilities: currentUtilities,
        emissions,
        convertCarbonUnits,
        sqFootage,
        electricCarbonIntensity,
        electricCarbonIntensityUnits,
        electricRenewablesPercentage,
        type: facilityType?.value,
        ...address
      });

      return {
        ...office,
        currentUtilities,
        emissions,
        facilityType: facilityType || { label: "Office" },
        status,
        severity,
        currentCommuteEmployees,
        ...monthlyCarbonVolumes
      };
    });
  };

  const displayOffices = formatDisplayOffices();

  return {
    offices: displayOffices,
    officesLoading: componentLoading
  };
};
